// Design System V2 ("Warm Precision" 2026 refresh) — reversible runtime flag.
//
// Reads a single localStorage flag once at app boot and stamps it onto
// <html data-theme-v2="on|off">. All v2 visual refinements in
// client/src/styles/design-tokens.css are scoped under
// `[data-theme-v2="on"]` — flipping this flag and reloading instantly
// reverts every ds/ component to its pre-refresh appearance with zero
// redeploy. Defaults to "on" if unset so the refresh ships live, but
// gives Alex (or any staff user) a one-click, no-deploy kill switch.
//
// Toggle UI: client/src/pages/Admin/DesignSystemV2/index.tsx
// (Velaro Admin nav → Velaro Admin → Design System (2026)).

export const DESIGN_V2_STORAGE_KEY = "velaroDesignV2";

export type DesignV2Value = "on" | "off";

function normalize(raw: string | null): DesignV2Value {
  return raw === "off" ? "off" : "on"; // default "on" for unset AND any unrecognized value
}

/** Read the current flag value without touching the DOM. */
export function getDesignV2(): DesignV2Value {
  try {
    return normalize(localStorage.getItem(DESIGN_V2_STORAGE_KEY));
  } catch {
    // localStorage can throw in locked-down/private-browsing contexts — fail
    // open to the new design rather than crash boot.
    return "on";
  }
}

/** Stamp the current flag value onto <html data-theme-v2>. Call once at boot, as early as possible. */
export function applyDesignV2ToDocument(): DesignV2Value {
  const value = getDesignV2();
  document.documentElement.dataset.themeV2 = value;
  return value;
}

/** Persist a new flag value. Callers are responsible for reloading (or re-applying) afterward. */
export function setDesignV2(value: DesignV2Value): void {
  try {
    localStorage.setItem(DESIGN_V2_STORAGE_KEY, value);
  } catch {
    // Best-effort — if storage is unavailable the toggle simply won't persist.
  }
  document.documentElement.dataset.themeV2 = value;
}
